import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { thawWindows } from '@goaltree/backend/convex/lib/goalFreeze'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { LegendList } from '@legendapp/list/react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { useAction, useMutation, useQuery } from 'convex/react'
import {
  BarChart3Icon,
  BellIcon,
  BookOpenIcon,
  Check,
  CheckCircleIcon,
  ClipboardListIcon,
  CompassIcon,
  FlameIcon,
  ImageIcon,
  LayersIcon,
  MicIcon,
  MoreVerticalIcon,
  PencilIcon,
  PlusIcon,
  RotateCcwIcon,
  SearchIcon,
  Share2Icon,
  SnowflakeIcon,
  StarIcon,
  TargetIcon,
  Trash2Icon,
  TrophyIcon,
  UploadIcon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { BrainDumpTip } from '@/components/brain-dump-tip'
import { BrandMark } from '@/components/brand-mark'
import { CoachLauncherFace } from '@/components/coach-avatar'
import { CoachingBadge } from '@/components/coaching/coaching-mode'
import {
  HumanCoachAvatar,
  MyCoachNotesSheet,
  openCoachNotes,
  useCoachInbox,
} from '@/components/coaching/my-coach-notes'
import { ComposerDialog } from '@/components/composer-dialog'
import { GoProCard } from '@/components/go-pro-card'
import { FreezeGoalDialog, goalFreezeState } from '@/components/goal-freeze'
import { NavMain } from '@/components/nav-main'
import { NavUser } from '@/components/nav-user'
import { NewMenu, useOpenNewGoal, useQuickAdd } from '@/components/new-menu'
import { ShareGoalDialog } from '@/components/share-goal-dialog'
import { useCoachNavMeta } from '@/hooks/use-coach-nav-meta'
import { useGoals } from '@/hooks/use-goals'
import { useReviewDue } from '@/hooks/use-review-due'
import { useSaveAsTemplate } from '@/hooks/use-save-as-template'
import {
  captureFieldShortcutLabel,
  isNewEventHotkey,
  isNewGoalHotkey,
  isNewHabitHotkey,
  isNewTaskHotkey,
} from '@/lib/capture'
import { CATEGORY_COLORS, categoryTextColor } from '@/lib/category-color'
import { useCoachCall } from '@/lib/coach-call-store'
import { useIsCoaching } from '@/lib/coaching-mode'
import { todayKey } from '@/lib/day'
import { patchGoal } from '@/lib/goals-optimistic'
import { optimisticRename, removeNodes } from '@/lib/nodes-optimistic'
import { useUploadCover } from '@/lib/upload-cover'
import { goalSlug, goalUrl } from '@/lib/utils'
import { openCapture } from '@/store/capture.store'
import { useUserMode } from '@/store/user-mode.store'
import { type GoalGroupKey, useViewStore } from '@/store/view.store'

type SidebarGoalItem = {
  id: string
  title: string
  url: string
  done: boolean
  owned: boolean
  category: string
  status?: 'backlog' | 'in-progress' | 'done'
  creationTime: number
  lastActivityAt?: number
  freezes?: { from: string; until?: string }[]
  isPublic: boolean
  taskCount: number
  remainingCount: number
}

const GOAL_STATUS_ORDER: Record<string, number> = { backlog: 0, 'in-progress': 1, done: 2 }

function groupSidebarItems(
  items: SidebarGoalItem[],
  group: GoalGroupKey,
  statusLabels: Record<string, string>,
  categoryLabels: Record<string, string>,
) {
  if (group === 'none') return [{ label: '', color: undefined, goals: items }]
  const map = new Map<string, SidebarGoalItem[]>()
  for (const item of items) {
    const key = group === 'category' ? item.category.toLowerCase() : (item.status ?? 'backlog')
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(item)
  }
  const entries = Array.from(map.entries())
  if (group === 'status')
    entries.sort(([a], [b]) => (GOAL_STATUS_ORDER[a] ?? 99) - (GOAL_STATUS_ORDER[b] ?? 99))
  return entries.map(([key, goals]) => ({
    label: group === 'category' ? (categoryLabels[key] ?? key) : (statusLabels[key] ?? key),
    color: group === 'category' ? CATEGORY_COLORS[key] : undefined,
    goals,
  }))
}

const QUOTE_INTERVAL = 8000
const FADE_DURATION = 600

function RotatingQuote() {
  const { t } = useTranslation()
  const quotes = t('sidebar.quotes', { returnObjects: true }) as string[]
  const [index, setIndex] = useState(() => Math.floor(Math.random() * quotes.length))
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timer = setInterval(() => {
      setVisible(false)
      setTimeout(() => {
        setIndex((i) => (i + 1) % quotes.length)
        setVisible(true)
      }, FADE_DURATION)
    }, QUOTE_INTERVAL)
    return () => clearInterval(timer)
  }, [quotes.length])

  return (
    <p
      className="truncate text-[11px] italic text-muted-foreground/60 transition-opacity group-data-[collapsible=icon]:hidden"
      style={{ opacity: visible ? 1 : 0, transitionDuration: `${FADE_DURATION}ms` }}
    >
      {quotes[index]}
    </p>
  )
}

function GoalListItem({
  item,
  isActive,
  isStarred,
  isMobile,
  frozen,
  hasCover,
  achieved,
  generatingCover,
  onStar,
  onRename,
  onDelete,
  onShare,
  onGenerateCover,
  onUploadCover,
  onRemoveCover,
  onSaveTemplate,
  onFreeze,
  onToggleAchieved,
}: {
  item: SidebarGoalItem
  isActive: boolean
  isStarred: boolean
  isMobile: boolean
  frozen: boolean
  hasCover: boolean
  achieved: boolean
  generatingCover: boolean
  onStar: () => void
  onRename: () => void
  onDelete: () => void
  onShare: () => void
  onGenerateCover: () => void
  onUploadCover: () => void
  onRemoveCover: () => void
  onSaveTemplate: () => void
  onFreeze: () => void
  onToggleAchieved: () => void
}) {
  const { t } = useTranslation()
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={isActive}
        render={<Link to={item.url} title={item.title} />}
        className="pr-1! relative"
      >
        <span
          aria-hidden
          className={cn('size-2 shrink-0 rounded-[3px]', item.done && 'opacity-40')}
          style={{
            backgroundColor: CATEGORY_COLORS[item.category.toLowerCase()]
              ? categoryTextColor(CATEGORY_COLORS[item.category.toLowerCase()])
              : 'var(--muted-foreground)',
          }}
        />
        <span className={cn('truncate pr-4 text-[13px]', item.done && 'line-through opacity-40')}>
          {item.title}
        </span>
        <span
          className={`pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-r from-transparent opacity-0 transition-opacity duration-150 group-hover/menu-item:opacity-100 group-has-[[aria-expanded=true]]/menu-item:opacity-100 ${isActive ? 'to-sidebar-accent' : 'to-sidebar'}`}
          aria-hidden
        />
      </SidebarMenuButton>
      {item.owned && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuAction
                showOnHover
                className="aria-expanded:opacity-100 cursor-pointer size-6! right-1!"
              />
            }
          >
            <MoreVerticalIcon className="h-4 w-4" />
            <span className="sr-only">More</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56"
            side={isMobile ? 'bottom' : 'right'}
            align={isMobile ? 'end' : 'start'}
          >
            <DropdownMenuItem className="cursor-pointer px-3 font-medium" onClick={onStar}>
              <StarIcon
                className={`text-muted-foreground${isStarred ? ' fill-current text-yellow-400' : ''}`}
              />
              <span>{isStarred ? t('sidebar.unstar') : t('sidebar.star')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer px-3 font-medium" onClick={onShare}>
              <Share2Icon className="text-muted-foreground" />
              <span>{t('common.share')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer px-3 font-medium"
              disabled={generatingCover}
              onClick={onGenerateCover}
            >
              <ImageIcon
                className={`text-muted-foreground${generatingCover ? ' animate-pulse' : ''}`}
              />
              <span>
                {generatingCover
                  ? t('common.generatingCover')
                  : hasCover
                    ? t('common.regenerateCover')
                    : t('common.generateCover')}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer px-3 font-medium" onClick={onUploadCover}>
              <UploadIcon className="text-muted-foreground" />
              <span>{t('common.uploadCover')}</span>
            </DropdownMenuItem>
            {hasCover && (
              <DropdownMenuItem className="cursor-pointer px-3 font-medium" onClick={onRemoveCover}>
                <XIcon className="text-muted-foreground" />
                <span>{t('common.removeCover')}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="cursor-pointer px-3 font-medium" onClick={onRename}>
              <PencilIcon className="text-muted-foreground" />
              <span>{t('sidebar.rename')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer px-3 font-medium" onClick={onSaveTemplate}>
              <LayersIcon className="text-muted-foreground" />
              <span>{t('templates.saveAsTemplate')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer px-3 font-medium" onClick={onFreeze}>
              <SnowflakeIcon className="text-sky-500" />
              <span>{t(frozen ? 'goalFreeze.unfreeze' : 'goalFreeze.freeze')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer px-3 font-medium"
              onClick={onToggleAchieved}
            >
              {achieved ? (
                <RotateCcwIcon className="text-muted-foreground" />
              ) : (
                <TrophyIcon className="text-amber-500" />
              )}
              <span>{t(achieved ? 'goalSheet.reopen' : 'goalSheet.markAchieved')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onClick={onDelete}
              className="cursor-pointer px-3 font-medium"
            >
              <Trash2Icon />
              <span>{t('common.delete')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </SidebarMenuItem>
  )
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation()
  const goals = useGoals()
  const goalsGroup = useViewStore((s) => s.goalsFilter.group)
  const setGoalsFilter = useViewStore((s) => s.setGoalsFilter)
  const { location } = useRouterState()
  const currentPath = location.pathname.replace(/\/$/, '') || '/'
  const { isMobile, setOpenMobile } = useSidebar()
  const navigate = useNavigate()
  const closeMobile = () => isMobile && setOpenMobile(false)

  const todayPendingCount = useQuery(api.dailySurface.getTodayPendingCount)
  const prefs = useQuery(api.preferences.getMyPreferences)
  const starredIds = new Set(
    (prefs as { starredGoalIds?: string[] } | null | undefined)?.starredGoalIds ?? [],
  )
  const sharedGoals = useQuery(api.sharing.getSharedGoals)
  const unreadCount = useQuery(api.notifications.unreadCount) ?? 0
  const reviewDue = useReviewDue()
  const myRank = useQuery(api.leaderboard.getMyLeaderboardRank)
  const clientCount = useQuery(api.coaching.myClientCount) ?? 0
  const coaching = useIsCoaching()

  const renameNode = useMutation(api.nodes.rename).withOptimisticUpdate(optimisticRename)
  const removeNode = useMutation(api.nodes.remove).withOptimisticUpdate((localStore, { nodeId }) =>
    removeNodes(localStore, [nodeId]),
  )
  const toggleStar = useMutation(api.preferences.toggleStarGoal).withOptimisticUpdate(
    (localStore, { goalId }) => {
      const current = localStore.getQuery(api.preferences.getMyPreferences, {})
      if (!current) return
      const ids = (current as { starredGoalIds?: string[] }).starredGoalIds ?? []
      const next = ids.includes(goalId) ? ids.filter((id) => id !== goalId) : [...ids, goalId]
      localStore.setQuery(
        api.preferences.getMyPreferences,
        {},
        { ...current, starredGoalIds: next },
      )
    },
  )
  const generateCover = useAction(api.goalImages.generateCover)
  const removeCover = useMutation(api.nodes.removeCoverImage)
  const uploadCover = useUploadCover()
  const saveAsTemplate = useSaveAsTemplate()
  const setGoalStatus = useMutation(api.nodes.setGoalStatus).withOptimisticUpdate(
    (store, { goalId, status }) => patchGoal(store, goalId, { status }),
  )
  const unfreezeGoal = useMutation(api.nodes.unfreezeGoal).withOptimisticUpdate(
    (store, { goalId }) => {
      const goal = store.getQuery(api.nodes.getUserGoals, {})?.find((g) => g._id === goalId)
      if (!goal) return
      const freezes = thawWindows(goal.freezes, todayKey())
      patchGoal(store, goalId, { freezes: freezes.length ? freezes : undefined })
    },
  )
  const covers = useQuery(api.nodes.getGoalCoverUrls)
  const coverIds = new Set((covers ?? []).map((c) => c.goalId))

  const [renameTarget, setRenameTarget] = useState<{ id: string; title: string } | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [shareTarget, setShareTarget] = useState<SidebarGoalItem | null>(null)
  const [shareOpen, setShareOpen] = useState(false)
  const [freezeId, setFreezeId] = useState<Id<'nodes'> | null>(null)
  const [freezeOpen, setFreezeOpen] = useState(false)
  const [generatingCoverId, setGeneratingCoverId] = useState<string | null>(null)
  const [postOpen, setPostOpen] = useState(false)
  useEffect(() => {
    const open = () => setPostOpen(true)
    window.addEventListener('open-new-post', open)
    return () => window.removeEventListener('open-new-post', open)
  }, [])
  const openNewGoal = useOpenNewGoal()
  const quickAdd = useQuickAdd()
  const skipNewGoalHotkey =
    currentPath.startsWith('/onboarding') || /^\/goals\/new(\/|$)/.test(currentPath)
  useEffect(() => {
    if (skipNewGoalHotkey) return
    const onKey = (e: KeyboardEvent) => {
      const kind = isNewTaskHotkey(e)
        ? 'tasks'
        : isNewHabitHotkey(e)
          ? 'habits'
          : isNewEventHotkey(e)
            ? 'event'
            : null
      if (kind) {
        e.preventDefault()
        void quickAdd(kind)
      } else if (isNewGoalHotkey(e)) {
        e.preventDefault()
        openNewGoal()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [skipNewGoalHotkey, openNewGoal, quickAdd])

  const initialItemIds = useRef<Set<string> | null>(null)

  async function commitRename() {
    if (!renameTarget) return
    const trimmed = renameValue.trim()
    if (trimmed && trimmed !== renameTarget.title) {
      await renameNode({ nodeId: renameTarget.id as Id<'nodes'>, title: trimmed })
      navigate({ to: goalUrl(trimmed, renameTarget.id as Id<'nodes'>) })
    }
    setRenameTarget(null)
  }

  async function commitDelete() {
    if (!deleteTarget) return
    setDeleteLoading(true)
    await removeNode({ nodeId: deleteTarget.id as Id<'nodes'> })
    if (currentPath.includes(deleteTarget.id)) navigate({ to: '/goals' })
    setDeleteLoading(false)
    setDeleteTarget(null)
  }

  const inboxGoal = goals?.find((g) => g.isInbox)

  const ownedItems: SidebarGoalItem[] = (goals ?? [])
    .filter((g) => !g.isInbox)
    .map((g) => ({
      id: g._id as string,
      title: g.title,
      url: goalUrl(g.title, g._id),
      done: g.taskCount > 0 && g.remainingCount === 0,
      owned: true,
      category: g.category,
      status: g.status,
      creationTime: g._creationTime,
      lastActivityAt: g.lastActivityAt,
      freezes: g.freezes,
      isPublic: g.isPublic,
      taskCount: g.taskCount,
      remainingCount: g.remainingCount,
    }))

  const statusLabels: Record<string, string> = {
    backlog: t('goals.backlog'),
    'in-progress': t('goals.inProgress'),
    done: t('goals.done'),
  }

  const categoryLabels: Record<string, string> = {
    health: t('goals.cat.health'),
    career: t('goals.cat.career'),
    mind: t('goals.cat.mind'),
    finance: t('goals.cat.finance'),
    relationships: t('goals.cat.relationships'),
    creative: t('goals.cat.creative'),
  }

  const starredItems = ownedItems.filter((i) => starredIds.has(i.id))
  const recentItems = ownedItems
    .filter((i) => !starredIds.has(i.id))
    .sort((a, b) => (b.lastActivityAt ?? b.creationTime) - (a.lastActivityAt ?? a.creationTime))

  if (goals !== undefined && initialItemIds.current === null) {
    initialItemIds.current = new Set(ownedItems.map((i) => i.id))
  }

  type SidebarListRow =
    | { kind: 'goal'; item: SidebarGoalItem }
    | { kind: 'header'; label: string; color?: string }

  const flatRecentData: SidebarListRow[] =
    goalsGroup === 'none'
      ? recentItems.map((item) => ({ kind: 'goal' as const, item }))
      : groupSidebarItems(recentItems, goalsGroup, statusLabels, categoryLabels).flatMap((g) => [
          ...(g.label ? [{ kind: 'header' as const, label: g.label, color: g.color }] : []),
          ...g.goals.map((item) => ({ kind: 'goal' as const, item })),
        ])

  const sharedItems: SidebarGoalItem[] = (sharedGoals ?? []).map((g) => ({
    id: g._id as string,
    title: g.title,
    url: goalUrl(g.title, g._id),
    done: false,
    owned: false,
    category: g.category,
    status: g.status,
    creationTime: g._creationTime,
    lastActivityAt: g.lastActivityAt,
    isPublic: false,
    taskCount: 0,
    remainingCount: 0,
  }))

  async function generateCoverFor(id: string) {
    setGeneratingCoverId(id)
    try {
      await generateCover({ goalId: id as Id<'nodes'> })
    } catch {
      toast.error(t('common.error'))
    } finally {
      setGeneratingCoverId(null)
    }
  }

  function toggleAchieved(item: SidebarGoalItem) {
    const done = item.status !== 'done'
    const reopen = item.taskCount > item.remainingCount ? 'in-progress' : 'backlog'
    const status = done ? 'done' : reopen
    const goalId = item.id as Id<'nodes'>
    void setGoalStatus({ goalId, status })
      .then(() => {
        if (!done) return
        toast.success(t('goalSheet.achievedToast'), {
          action: {
            label: t('common.undo'),
            onClick: () => void setGoalStatus({ goalId, status: reopen }),
          },
        })
      })
      .catch(() => toast.error(t('common.error')))
  }

  function renderGoalItem(item: SidebarGoalItem, _index: number) {
    const { frozen } = goalFreezeState(item.freezes)
    const goalId = item.id as Id<'nodes'>
    return (
      <GoalListItem
        key={item.id}
        item={item}
        isActive={currentPath === (item.url.replace(/\/$/, '') || '/')}
        isStarred={starredIds.has(item.id)}
        isMobile={isMobile}
        frozen={frozen}
        hasCover={coverIds.has(item.id)}
        achieved={item.status === 'done'}
        generatingCover={generatingCoverId === item.id}
        onStar={() => toggleStar({ goalId: item.id })}
        onRename={() => {
          setRenameValue(item.title)
          setRenameTarget({ id: item.id, title: item.title })
        }}
        onDelete={() => setDeleteTarget({ id: item.id, title: item.title })}
        onShare={() => {
          setShareTarget(item)
          setShareOpen(true)
        }}
        onGenerateCover={() => void generateCoverFor(item.id)}
        onUploadCover={() => void uploadCover(goalId)}
        onRemoveCover={() => {
          removeCover({ goalId }).catch(() => toast.error(t('common.error')))
        }}
        onSaveTemplate={() => void saveAsTemplate(goalId)}
        onFreeze={() => {
          if (frozen) {
            void unfreezeGoal({ goalId })
              .then(() => toast.success(t('goalFreeze.unfrozenToast')))
              .catch(() => toast.error(t('common.error')))
            return
          }
          setFreezeId(goalId)
          setFreezeOpen(true)
        }}
        onToggleAchieved={() => toggleAchieved(item)}
      />
    )
  }

  const openSearch = () => window.dispatchEvent(new CustomEvent('open-search'))
  const startBrainDump = () => {
    if (isMobile) setOpenMobile(false)
    openCapture()
  }

  const activeGoalCount = (goals ?? []).filter((g) => !g.isInbox && g.status !== 'done').length

  // Coach persona label: name + effective speech style, which shifts with
  // progress (e.g. a broken streak nudges the style to strict).
  const coachMeta = useCoachNavMeta()
  const coachInbox = useCoachInbox(!coaching)

  const navItems = [
    {
      title: t('nav.today'),
      url: '/today',
      icon: <CheckCircleIcon />,
      count: todayPendingCount ?? 0,
    },
    {
      title: t('nav.goals'),
      url: '/goals',
      icon: <TargetIcon />,
      exact: true,
      count: activeGoalCount,
    },
    {
      title: t('nav.inbox'),
      url: '/everyday',
      icon: <FlameIcon />,
      count: inboxGoal?.remainingCount ?? 0,
    },
    { title: t('nav.discover'), url: '/explore', icon: <CompassIcon /> },
  ]
  const communityItems = [
    {
      title: t('nav.leaderboard'),
      url: '/leaderboard',
      icon: <TrophyIcon />,
      meta: myRank ? `#${myRank.entry.rank}${myRank.capped ? '+' : ''}` : undefined,
    },
    ...(clientCount > 0
      ? [{ title: t('nav.coaching'), url: '/coaching', icon: <UsersIcon />, count: clientCount }]
      : []),
  ]
  const coachingItems = [
    { title: t('coachingMode.clients'), url: '/coaching', icon: <UsersIcon />, count: clientCount },
    { title: t('coachingMode.plans'), url: '/coaching/plans', icon: <TargetIcon /> },
    { title: t('coachingMode.library'), url: '/coaching/library', icon: <CompassIcon /> },
    {
      title: t('coachingMode.inviteClient'),
      icon: <UserPlusIcon />,
      onClick: () => {
        closeMobile()
        useUserMode.getState().requestInvite(true)
        void navigate({ to: '/coaching' })
      },
    },
  ]
  const reflectItems = [
    {
      title: t('coach.title'),
      icon: <CoachLauncherFace size={20} />,
      meta: coachMeta,
      onClick: () => {
        if (isMobile) setOpenMobile(false)
        useCoachCall.getState().openSheet()
      },
    },
    ...(coachInbox
      ? [
          {
            title: t('coach.coachLabel', { name: coachInbox.coach.name }),
            icon: (
              <HumanCoachAvatar
                image={coachInbox.coach.image}
                name={coachInbox.coach.name}
                size={18}
                dot={coachInbox.unread}
              />
            ),
            onClick: () => {
              if (isMobile) setOpenMobile(false)
              openCoachNotes()
            },
          },
        ]
      : []),
    { title: t('nav.stats'), url: '/stats', icon: <BarChart3Icon /> },
    { title: t('nav.journal'), url: '/journal', icon: <BookOpenIcon /> },
    {
      title: t('nav.review'),
      url: '/review',
      icon: <ClipboardListIcon />,
      dot: reviewDue,
      dotLabel: t('nav.reviewDue'),
    },
  ]

  return (
    <>
      <Sidebar
        collapsible="icon"
        className="[&_[data-sidebar=rail]]:pointer-events-none [&_[data-sidebar=rail]]:hidden"
        {...props}
      >
        <SidebarHeader className="gap-0 px-3 pt-3 pb-1 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-2">
          <div className="flex items-center gap-2.5 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-1">
            <Link
              to={coaching ? '/coaching' : '/today'}
              onClick={closeMobile}
              aria-label="Goaltree"
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring group-data-[collapsible=icon]:flex-none"
            >
              <BrandMark className="group-data-[collapsible=icon]:size-8" />
              <span className="truncate font-logo text-[17px] font-extrabold tracking-tight text-foreground group-data-[collapsible=icon]:hidden">
                Goaltree
              </span>
              <CoachingBadge className="group-data-[collapsible=icon]:hidden" />
            </Link>
            <Link
              to="/notifications"
              onClick={closeMobile}
              aria-label={
                unreadCount > 0
                  ? `${t('nav.notifications')} (${unreadCount})`
                  : t('nav.notifications')
              }
              title={t('nav.notifications')}
              className={cn(
                'relative flex size-[30px] shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground group-data-[collapsible=icon]:mr-0',
                currentPath === '/notifications' && 'bg-sidebar-accent text-foreground',
              )}
            >
              <BellIcon className="size-4" />
              {unreadCount > 0 && (
                <span className="absolute top-[5px] right-[6px] size-[7px] rounded-full bg-primary ring-2 ring-sidebar" />
              )}
            </Link>
          </div>
          <RotatingQuote />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup className="px-3 pt-1 pb-1 group-data-[collapsible=icon]:hidden">
            <div className="relative flex h-10 items-center gap-0.5 rounded-xl border border-sidebar-border bg-muted/40 pr-1 pl-3 transition-colors hover:bg-muted/60">
              <button
                type="button"
                onClick={openSearch}
                className="flex min-w-0 flex-1 cursor-text items-center gap-2 self-stretch text-left outline-none"
              >
                <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate text-sm text-muted-foreground">
                  {t('nav.searchPlaceholder')}
                </span>
                {!isMobile && (
                  <kbd className="inline-flex shrink-0 items-center gap-0.5 font-medium text-muted-foreground/60">
                    <span className="text-base leading-none">⌘</span>
                    <span className="text-xs leading-none">K</span>
                  </kbd>
                )}
              </button>
              <span aria-hidden className="mx-1.5 h-4 w-px bg-sidebar-border" />
              <NewMenu
                align="end"
                showShortcut={!isMobile}
                onPick={() => isMobile && setOpenMobile(false)}
                trigger={
                  <button
                    type="button"
                    aria-label={t('nav.new')}
                    title={t('nav.new')}
                    className="flex size-8 cursor-pointer items-center justify-center rounded-lg text-foreground transition-colors hover:bg-muted aria-expanded:bg-muted"
                  />
                }
              >
                <PlusIcon className="size-4" strokeWidth={2.5} />
              </NewMenu>
              {!coaching && (
                <button
                  type="button"
                  aria-label={t('voice.title')}
                  title={`${t('voice.title')} (${captureFieldShortcutLabel()})`}
                  onClick={startBrainDump}
                  className="flex size-8 cursor-pointer items-center justify-center rounded-lg text-primary-text transition-colors hover:bg-primary/10"
                >
                  <MicIcon className="size-4" strokeWidth={2.5} />
                </button>
              )}
              {!isMobile && !coaching && <BrainDumpTip placement="below" />}
            </div>
          </SidebarGroup>

          <SidebarGroup className="hidden px-2 py-1 group-data-[collapsible=icon]:flex">
            <SidebarMenu className="gap-1">
              {!coaching && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip={`${t('voice.title')} (${captureFieldShortcutLabel()})`}
                    onClick={startBrainDump}
                    className="cursor-pointer text-primary-text"
                  >
                    <MicIcon strokeWidth={2.5} />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <NewMenu
                  side="right"
                  trigger={
                    <SidebarMenuButton
                      aria-label={t('nav.new')}
                      title={t('nav.new')}
                      className="cursor-pointer"
                    />
                  }
                >
                  <PlusIcon strokeWidth={2.5} />
                </NewMenu>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={t('nav.search')}
                  onClick={openSearch}
                  className="cursor-pointer"
                >
                  <SearchIcon />
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>

          {/* Main nav */}
          {coaching ? (
            <NavMain label={t('coachingMode.section')} items={coachingItems} />
          ) : (
            <>
              <NavMain items={navItems} />
              <NavMain label={t('nav.sectionCommunity')} items={communityItems} />
              <NavMain label={t('nav.sectionReflect')} items={reflectItems} />
            </>
          )}

          {/* Starred */}
          {starredItems.length > 0 && (
            <SidebarGroup className="px-3 py-1 group-data-[collapsible=icon]:hidden">
              <SidebarGroupLabel className="h-6 px-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                {t('nav.starred')}
              </SidebarGroupLabel>
              <AnimatePresence mode="popLayout">
                <div className="mt-1">
                  <LegendList
                    data={starredItems}
                    keyExtractor={(item) => item.id}
                    renderItem={({ item }) => (
                      <motion.div
                        key={item.id}
                        layout
                        layoutDependency={goalsGroup}
                        initial={
                          initialItemIds.current?.has(item.id) ? false : { opacity: 0, y: 6 }
                        }
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -16 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                      >
                        {renderGoalItem(item, 0)}
                      </motion.div>
                    )}
                    recycleItems={false}
                    estimatedItemSize={30}
                    style={{ overflow: 'visible' }}
                  />
                </div>
              </AnimatePresence>
            </SidebarGroup>
          )}

          {/* Shared */}
          {sharedItems.length > 0 && (
            <SidebarGroup className="px-3 py-1 group-data-[collapsible=icon]:hidden">
              <SidebarGroupLabel className="h-6 px-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                {t('nav.shared')}
              </SidebarGroupLabel>
              <SidebarMenu className="gap-0.5 mt-1">
                {sharedItems.map((item, i) => renderGoalItem(item, i))}
              </SidebarMenu>
            </SidebarGroup>
          )}

          {/* Recents */}
          {recentItems.length > 0 && (
            <SidebarGroup className="px-3 py-1 group-data-[collapsible=icon]:hidden">
              <SidebarGroupLabel className="h-6 justify-between px-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                <span>{t('nav.recents')}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <button
                        type="button"
                        title={t('filter.groupBy')}
                        className={`flex cursor-pointer items-center rounded p-0.5 transition-colors hover:text-muted-foreground ${goalsGroup !== 'none' ? 'text-primary-text' : 'text-muted-foreground/40'}`}
                      />
                    }
                  >
                    <LayersIcon className="h-3 w-3" />
                    <span className="sr-only">{t('filter.groupBy')}</span>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-40 rounded-lg" side="right" align="start">
                    {(['none', 'category', 'status'] as const).map((opt) => (
                      <DropdownMenuItem
                        key={opt}
                        onClick={() => setGoalsFilter({ group: opt })}
                        className="cursor-pointer gap-2"
                      >
                        <Check
                          className={`h-3.5 w-3.5 shrink-0 ${goalsGroup === opt ? 'opacity-100' : 'opacity-0'}`}
                        />
                        {opt === 'none'
                          ? t('filter.noGrouping')
                          : opt === 'category'
                            ? t('filter.groupCategory')
                            : t('filter.groupStatus')}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarGroupLabel>
              <AnimatePresence mode="popLayout">
                <div className="mt-1">
                  <LegendList
                    data={flatRecentData}
                    keyExtractor={(row) =>
                      row.kind === 'goal' ? row.item.id : `header-${row.label}`
                    }
                    renderItem={({ item: row }) => {
                      if (row.kind === 'header') {
                        return (
                          <motion.div
                            key={`header-${row.label}`}
                            layout
                            layoutDependency={goalsGroup}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                            className="px-2 pt-2 pb-0.5 text-[10px] font-bold uppercase tracking-widest"
                            style={
                              row.color
                                ? { color: categoryTextColor(row.color) }
                                : { color: 'hsl(var(--muted-foreground))' }
                            }
                          >
                            {row.label}
                          </motion.div>
                        )
                      }
                      return (
                        <motion.div
                          key={row.item.id}
                          layout
                          layoutDependency={goalsGroup}
                          initial={
                            initialItemIds.current?.has(row.item.id) ? false : { opacity: 0, y: 6 }
                          }
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, x: -16 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                        >
                          {renderGoalItem(row.item, 0)}
                        </motion.div>
                      )
                    }}
                    recycleItems={false}
                    estimatedItemSize={30}
                    style={{ overflow: 'visible' }}
                  />
                </div>
              </AnimatePresence>
            </SidebarGroup>
          )}
        </SidebarContent>
        <SidebarFooter>
          <GoProCard compact className="group-data-[collapsible=icon]:hidden" />
          <NavUser />
        </SidebarFooter>
      </Sidebar>

      {/* Rename modal */}
      <AlertDialog open={!!renameTarget} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sidebar.renameGoal')}</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">
              {t('sidebar.renameDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename()
              if (e.key === 'Escape') setRenameTarget(null)
            }}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/50"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRenameTarget(null)}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction variant="default" onClick={commitRename}>
              {t('sidebar.rename')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm modal */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sidebar.deleteGoalTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('sidebar.deleteGoalDesc', { title: deleteTarget?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={commitDelete} disabled={deleteLoading}>
              {deleteLoading ? t('sidebar.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MyCoachNotesSheet />
      <ComposerDialog open={postOpen} onOpenChange={setPostOpen} />
      {shareTarget && (
        <ShareGoalDialog
          goalId={shareTarget.id as Id<'nodes'>}
          goalSlug={goalSlug(shareTarget.title, shareTarget.id)}
          isPublic={shareTarget.isPublic}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}
      {freezeId && (
        <FreezeGoalDialog goalId={freezeId} open={freezeOpen} onOpenChange={setFreezeOpen} />
      )}
    </>
  )
}
